import React, { useState } from "react";
import { useLanguage, useT } from "../../lib/LanguageContext";
import { ACCOUNT_STATES, AFFILIATE_PATHS } from "../../affiliate/contract";
import { formatMoneyCode } from "../../lib/formatMoney";
import LoadFailure from "../../components/ui/LoadFailure";
import usePagedList from "../../hooks/usePagedList";
import Pager from "../../components/ui/Pager";
import { affiliateErrorText } from "../../admin/affiliate/errorText";
import { fmtDate, EMPTY } from "../../admin/affiliate/format";

export const ACCOUNT_STATE_KEYS = {
  active: "saff.accountState.active",
  disabled: "saff.accountState.disabled",
};
const ACCOUNT_STATE_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  disabled: "bg-rose-50 text-rose-700 border-rose-200",
};

/**
 * Every practice that holds an affiliate account.
 *
 * `q` IS AN EXACT MATCH on `code` or `tenantId`, never a prefix and never a regex — the
 * same rule `gateway.repository.list_payments` follows, because a substring match across
 * every practice turns a console into an enumeration oracle. The placeholder says which two
 * things it will match, so an operator is not left guessing why a name found nothing.
 *
 * The search is submitted, not typed-through: a request per keystroke against an exact
 * match would be hundreds of certain misses.
 */
export default function AffiliatesTable({ onOpen }) {
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [status, setStatus] = useState("");
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");

  const list = usePagedList(AFFILIATE_PATHS.adminAffiliates, {
    ...(q ? { q } : {}),
    ...(status ? { status } : {}),
  });

  return (
    <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="sa-affiliates">
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-slate-200">
        <h2 className="text-xl text-slate-800">{t("saff.affiliates.title")}</h2>
        <div className="flex gap-2">
          <form
            onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); }}
            className="flex gap-2"
            data-testid="sa-affiliates-search-form"
          >
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={t("saff.affiliates.searchPlaceholder")}
              data-testid="sa-affiliates-search"
              className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
            />
            <button
              type="submit"
              data-testid="sa-affiliates-search-submit"
              className="text-xs px-3 py-1.5 rounded-lg border border-slate-200"
            >
              {t("saff.affiliates.search")}
            </button>
          </form>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="sa-affiliates-status-filter"
            className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">{t("saff.affiliates.filterAllStates")}</option>
            {ACCOUNT_STATES.map((s) => (
              <option key={s} value={s}>{t(ACCOUNT_STATE_KEYS[s])}</option>
            ))}
          </select>
        </div>
      </div>

      {list.loadError ? (
        <div className="p-5">
          <LoadFailure
            message={affiliateErrorText(list.loadError, { t, tv, prefix: "saff", fallback: tv("errors.loadFailed") })}
            onRetry={list.reload}
            retryLabel={tv("actions.retry")}
            testId="sa-affiliates-load-error"
            retryTestId="sa-affiliates-retry"
          />
        </div>
      ) : list.loaded && list.rows.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm" data-testid="sa-affiliates-empty">
          {t("saff.affiliates.empty")}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-400 bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2">{t("saff.affiliates.colPractice")}</th>
                  <th className="text-left px-4 py-2">{t("saff.affiliates.colCode")}</th>
                  <th className="text-left px-4 py-2">{t("saff.affiliates.colStatus")}</th>
                  <th className="text-right px-4 py-2">{t("saff.affiliates.colClicks")}</th>
                  <th className="text-right px-4 py-2">{t("saff.affiliates.colReferrals")}</th>
                  <th className="text-right px-4 py-2">{t("saff.affiliates.colAccrued")}</th>
                  <th className="text-right px-4 py-2">{t("saff.affiliates.colReversed")}</th>
                  <th className="text-left px-4 py-2">{t("saff.affiliates.colCreatedAt")}</th>
                  <th className="text-right px-4 py-2">{t("saff.affiliates.colAction")}</th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((a) => (
                  <tr key={a.tenantId} className="border-t border-slate-100" data-testid={`sa-affiliate-${a.tenantId}`}>
                    <td className="px-4 py-2 text-slate-800">{a.practiceName || a.tenantId}</td>
                    <td className="px-4 py-2 font-mono text-slate-500">{a.code || EMPTY}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${
                        ACCOUNT_STATE_STYLES[a.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                        {ACCOUNT_STATE_KEYS[a.status] ? t(ACCOUNT_STATE_KEYS[a.status]) : a.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{a.clicksTotal}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{a.referrals}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatMoneyCode(a.accruedCents, a.currency)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-rose-700">
                      {a.reversedCents ? formatMoneyCode(a.reversedCents, a.currency) : EMPTY}
                    </td>
                    <td className="px-4 py-2 text-slate-500">{fmtDate(a.createdAt, lang)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => onOpen(a.tenantId)}
                        data-testid={`sa-affiliate-open-${a.tenantId}`}
                        className="text-xs px-2 py-1 rounded border border-slate-200"
                      >
                        {t("saff.affiliates.view")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={list.page}
            pageSize={list.pageSize}
            total={list.total}
            shown={list.rows.length}
            onPage={list.setPage}
            infoText={(vars) => t("saff.page.info", vars)}
            prevLabel={t("saff.page.prev")}
            nextLabel={t("saff.page.next")}
            testId="sa-affiliates-page"
            tone="slate"
          />
        </>
      )}
    </section>
  );
}
